import * as React from 'react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreVertical,
  Eye,
  Edit2,
  Trash2,
  CheckCircle2,
  Clock,
  XCircle,
  Pause,
  Send,
  MessageSquare,
  Image,
  FileText as FileIcon,
  Video,
} from 'lucide-react'
import type { WATemplate, WATemplateStatus, WATemplateCategory } from '@/lib/api-client'

interface TemplateCardProps {
  template: WATemplate
  onView?: () => void
  onEdit?: () => void
  onDelete?: () => void
  onSend?: () => void
}

export function TemplateCard({
  template,
  onView,
  onEdit,
  onDelete,
  onSend,
}: TemplateCardProps) {
  const statusConfig = getStatusConfig(template.status)
  const categoryLabel = getCategoryLabel(template.category)

  // Count variables in body
  const variableCount = countVariables(template.bodyContent)

  return (
    <div className="flex flex-col p-4 rounded-lg border border-border bg-card hover:bg-hover transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-text-dark truncate">
            {template.name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <Badge
              variant="outline"
              className={cn('text-[10px] px-1.5 py-0 h-5', statusConfig.className)}
            >
              {statusConfig.icon}
              <span className="ml-1">{statusConfig.label}</span>
            </Badge>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
              {categoryLabel}
            </Badge>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {onView && (
              <DropdownMenuItem onClick={onView}>
                <Eye className="w-4 h-4 mr-2" />
                View
              </DropdownMenuItem>
            )}
            {onEdit && (
              <DropdownMenuItem onClick={onEdit}>
                <Edit2 className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
            )}
            {onSend && template.status === 'approved' && (
              <DropdownMenuItem onClick={onSend}>
                <Send className="w-4 h-4 mr-2" />
                Send
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Preview */}
      <div className="flex-1 mb-3">
        <div className="p-3 rounded-md bg-muted/30 text-xs text-text-muted">
          {/* Header - media or text */}
          {template.headerType && template.headerType !== 'text' ? (
            <div className="mb-2">
              {template.headerType === 'image' && template.headerContent ? (
                <img
                  src={template.headerContent}
                  alt="Template header"
                  className="w-full h-28 object-cover rounded"
                />
              ) : (
                <div className="flex items-center gap-2 p-2 rounded bg-muted/50 text-text-muted">
                  {template.headerType === 'video' ? (
                    <Video className="w-4 h-4" />
                  ) : (
                    <FileIcon className="w-4 h-4" />
                  )}
                  <span className="text-[10px] capitalize">{template.headerType} header</span>
                </div>
              )}
            </div>
          ) : template.headerContent ? (
            <p className="font-medium text-text-dark mb-1">
              {template.headerContent}
            </p>
          ) : null}
          <p className="line-clamp-3 whitespace-pre-wrap">
            {highlightVariables(template.bodyContent)}
          </p>
          {template.footerContent && (
            <p className="mt-1 text-[10px] opacity-60">
              {template.footerContent}
            </p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-[10px] text-text-muted pt-2 border-t border-border/50">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <MessageSquare className="w-3 h-3" />
            {variableCount} variables
          </span>
          <span>{template.language.toUpperCase()}</span>
        </div>
        <div className="flex items-center gap-2">
          <span>{template.totalSent.toLocaleString()} sent</span>
        </div>
      </div>
    </div>
  )
}

// Helper functions

function getStatusConfig(status: WATemplateStatus): {
  label: string
  icon: React.ReactNode
  className: string
} {
  switch (status) {
    case 'approved':
      return {
        label: 'Approved',
        icon: <CheckCircle2 className="w-3 h-3" />,
        className: 'text-success border-success/30 bg-success/5',
      }
    case 'pending':
      return {
        label: 'Pending',
        icon: <Clock className="w-3 h-3" />,
        className: 'text-warning border-warning/30 bg-warning/5',
      }
    case 'rejected':
      return {
        label: 'Rejected',
        icon: <XCircle className="w-3 h-3" />,
        className: 'text-destructive border-destructive/30 bg-destructive/5',
      }
    case 'paused':
      return {
        label: 'Paused',
        icon: <Pause className="w-3 h-3" />,
        className: 'text-text-muted border-border bg-muted/20',
      }
    default:
      return {
        label: status,
        icon: null,
        className: '',
      }
  }
}

function getCategoryLabel(category: WATemplateCategory): string {
  switch (category) {
    case 'MARKETING':
      return 'Marketing'
    case 'UTILITY':
      return 'Utility'
    case 'AUTHENTICATION':
      return 'Authentication'
    default:
      return category
  }
}

function countVariables(text: string): number {
  const matches = text.match(/\{\{(\d+)\}\}/g)
  if (!matches) return 0
  const numbers = matches.map(m => parseInt(m.replace(/\D/g, ''), 10))
  return Math.max(...numbers)
}

function highlightVariables(text: string): React.ReactNode {
  // Split by {{n}} pattern and create spans
  const parts = text.split(/(\{\{\d+\}\})/g)
  return parts.map((part, index) => {
    if (/\{\{\d+\}\}/.test(part)) {
      return (
        <span key={index} className="px-1 py-0.5 rounded bg-info/10 text-info font-mono">
          {part}
        </span>
      )
    }
    return part
  })
}
